import React, { useEffect } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

// Utils
import { WOC_Winners } from "../../utils/data/winners/winter-of-code";

// Material UI Components
import {
  Box,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  TableContainer,
  Grid,
} from "@mui/material";

gsap.registerPlugin(ScrollTrigger);

const panelColors = ["white", "black"];

function Winners() {
  useEffect(() => {
    gsap.utils.toArray(".panel").forEach((panel) => {
      gsap.fromTo(
        panel,
        { opacity: 0, scale: 0.9 },
        {
          opacity: 1,
          scale: 1,
          duration: 1,
          ease: "power2.out",
          scrollTrigger: {
            trigger: panel,
            start: "top 85%",
            toggleActions: "play none none reverse",
          },
        }
      );
    });
  }, []);

  useEffect(() => {
    // Split text into spans for each character
    const textElement = document.querySelector(".t-over");
    const text = textElement.textContent;
    textElement.innerHTML = text
      .split("")
      .map(
        (char, index) =>
          `<span class='char' style='display: inline-block;'>${char}</span>`
      )
      .join("");

    const chars = document.querySelectorAll(".char");

    const tl = gsap.timeline({ repeat: 0 });

    // Rotate each character individually
    tl.to(chars, {
      rotateY: 360,
      duration: 1,
      ease: "circ.inOut",
      stagger: 0.1, // Stagger effect for sequential animation
    }).to(chars, {
      rotateX: 360,
      duration: 1,
      ease: "circ.inOut",
      yoyo: true,
      stagger: 0.1, // Stagger effect for sequential animation
    });
  }, []);

  return (
    <Box sx={{ p: { xs: 2, sm: 4, md: 6 }, minHeight: "100vh" }}>
      <Typography
        variant="h3"
        align="center"
        color="white"
        gutterBottom
        fontWeight="bold"
        sx={{
          fontSize: { xs: "1.2rem", sm: "1.5rem", md: "2rem" },
          textAlign: "center",
        }}
      >
        🏆{" "}
        <span className="t-over">
          Winter&nbsp;of&nbsp;Code&nbsp;-&nbsp;Winners
        </span>{" "}
        🏆
      </Typography>

      {WOC_Winners.map((winner, index) => (
        <Box
          key={index}
          className="panel"
          sx={{
            my: { xs: 4, md: 6 },
            p: { xs: 2, sm: 4 },
            borderRadius: 4,
            bgcolor: panelColors[index % panelColors.length],
            boxShadow: 3,
            width: "100%",
            maxWidth: "1200px",
            mx: "auto",
            textAlign: "center",
          }}
        >
          <Typography
            variant="h4"
            sx={{
              color: panelColors[index % panelColors.length],
              bgcolor: panelColors[(index + 1) % panelColors.length],
              width: "fit-content",
              mx: "auto",
              px: 3,
              py: 1,
              my: 2,
              borderRadius: "20px",
              fontSize: { xs: "1.2rem", md: "1.5rem" },
            }}
            fontWeight="bold"
          >
            {winner.category}
          </Typography>

          {/* Winners Table */}
          {["Final Evaluation Winners", "Mid Evaluation Winners"].map(
            (title, idx) => (
              <Box key={idx} sx={{ mt: 4 }}>
                <Typography
                  variant="h5"
                  sx={{
                    color:
                      idx === 0
                        ? index % 2
                          ? "gold"
                          : "#fb8500"
                        : index % 2
                        ? "silver"
                        : "grey",
                    mb: 1,
                    fontWeight: "bold",
                    fontSize: { xs: "1.1rem", md: "1.3rem" },
                  }}
                >
                  {idx === 0 ? "🥇" : "🥈"} {title}
                </Typography>
                <TableContainer
                  component={Paper}
                  sx={{
                    bgcolor: "#333",
                    p: { xs: 1, sm: 2, md: 2 },
                    overflowX: "auto",
                  }}
                >
                  <Table>
                    <TableHead>
                      <TableRow sx={{ bgcolor: "#444" }}>
                        {["Position", "Name", "Student ID", "Prize"].map(
                          (header) => (
                            <TableCell
                              key={header}
                              sx={{
                                color: "white",
                                fontWeight: "bold",
                                textAlign: "center",
                                fontSize: { xs: "0.8rem", md: "1rem" },
                              }}
                            >
                              {header}
                            </TableCell>
                          )
                        )}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(idx === 0 ? winner.FW : winner.MW).map((w, i) => (
                        <TableRow key={i}>
                          {["position", "name", "student_id", "price"].map(
                            (field) => (
                              <TableCell
                                key={field}
                                sx={{
                                  color: "white",
                                  textAlign: "center",
                                  fontSize: { xs: "0.75rem", md: "1rem" },
                                }}
                              >
                                <Typography
                                  sx={{
                                    display: "flex",
                                    justifyContent: "center",
                                  }}
                                >
                                  {field == "price" ? (
                                    <span style={{ marginRight: "2px" }}>
                                      &#8377;
                                    </span>
                                  ) : (
                                    ""
                                  )}{" "}
                                  {w[field]}
                                </Typography>
                              </TableCell>
                            )
                          )}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )
          )}
        </Box>
      ))}
    </Box>
  );
}

export default Winners;
